import React, { useState, useEffect } from "react";
import { useUser } from "../UserContext"; // User context
import { useNavigate } from "react-router-dom";
import config from "../config"; // Import the config file

const Dashboard = () => {
  let HOME_PIC1 = require('../assets/GPPAF-LOGO.png');
  let HOS_STAMP = require('../assets/HOS_Stmp.jpg');
  let Counsellor = require('../assets/COUNSELLOR_SIGN.jpg')
  const { user } = useUser(); // Access user data from context
  const [studentData, setStudentData] = useState(null); // Store student's full data
  const [subjects, setSubjects] = useState([]); // Store subjects for the user's class
  const [results, setResults] = useState([]); // Student's results
  const [loading, setLoading] = useState(false); // Loading state
  const [error, setError] = useState(null); // Error state
  const markObtained = results.reduce((total, item) => total + item.Score, 0); // Sum of all scores
  const averageScore = (markObtained / 1600) * 100; // Average as a percentage
  const navigate = useNavigate();

  // Fetch student data from the server
  const fetchStudentData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${config.apiEndpoint}/${user?.Class}/${user?.id}`);
      if (!response.ok) {
        setError("Failed to load student data.");
        return;
      }

      const currentStudent = await response.json();

      if (!currentStudent) {
        throw new Error("Student data not found.");
      }

      setStudentData(currentStudent);
      setResults(currentStudent.Results || []); // Ensure results is an array
    } catch (err) {
      console.error("Error fetching student data:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch subjects for the user's section
  const fetchSubjects = async () => {
    if (!studentData) return; // Only fetch subjects after studentData is set

    try {
      const response = await fetch("/dataSubjects.json"); // Adjust the path as needed
      if (!response.ok) throw new Error("Failed to load subjects data.");
      const data = await response.json();

      
      if (!subjects) {
        throw new Error(`No subjects found for section: ${studentData.Section}.`);
      }

      setSubjects(data);
      setError(null); // Clear any previous errors
    } catch (err) {
      console.error("Error fetching subjects:", err);
      setError(err.message);
    }
  };

  // Generate results based on the subjects
  const generateResults = () => {
    // Ensure studentData and its Section are available
    if (!studentData || !studentData.Section) {
        console.error("Student data or Section is missing.");
        setError("Unable to generate results due to missing student data.");
        return;
    }

    // Ensure subjects have been fetched properly
    const sectionSubjects = subjects[studentData.Section]?.subjects || [];
    if (sectionSubjects.length === 0) {
        console.error("No subjects available for generating results.");
        setError("No subjects available to generate results.");
        return;
    }

    const newResults = sectionSubjects.map((subject) => {
        const ca1 = 0; // Placeholder for CA1 marks
        const ca2 = 0; // Placeholder for CA2 marks
        const classWork = 0; // Placeholder for Classwork marks
        const project = 0; // Placeholder for Project marks
        const exam = 0; // Placeholder for Exam marks
        const score = ca1 + ca2 + classWork + project + exam; // Total Score
        const comment =
            score >= 85
                ? "Excellent Work"
                : score >= 70
                ? "Good Effort"
                : score >= 50
                ? "Needs Improvement"
                : "No Comment";

        return {
            Subject: subject,
            CA1: ca1,
            CA2: ca2,
            ClassWork: classWork,
            Project: project,
            Exam: exam,
            Score: score,
            Comment: comment,
            Date: new Date().toLocaleString("en-US", {
                timeZone: "UTC",
                dateStyle: "full",
                timeStyle: "long",
            }),
        };
    });

    setResults(newResults);
    saveResultsToServer(newResults);
};


  // Save generated results to the server
  const saveResultsToServer = async (newResults) => {
    try {
      await fetch(`${config.apiEndpoint}/${user?.Class}/${user?.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...user,
          Results: newResults, // Ensure that the updated results are sent
        }),
      });
      console.log("Results saved successfully:", newResults);
    } catch (err) {
      console.error("Error saving results to server:", err);
      setError("Failed to save results.");
    }
  };

  // Handle result updates for individual subjects
  const updateResult = (subjectName, updatedResult) => {
    const updatedResults = results.map((result) =>
      result.Subject === subjectName ? { ...result, ...updatedResult } : result
    );
    setResults(updatedResults);
    saveResultsToServer(updatedResults); // Save the updated results to the server
  };

  // Initial data fetch
  useEffect(() => {
    if (user?.id) {
      fetchStudentData(); // Fetch student data
    }
  }, [user]);

  // Fetch subjects once student data is set
  useEffect(() => {
    if (studentData) {
      fetchSubjects(); // Fetch subjects for the user's section
    }
  }, [studentData]);

  // Logout function
  const handleLogout = () => {
    localStorage.removeItem("user");
    navigate("/"); // Redirect to the homepage or login
  };

  return (
    <div className="bg-gray-100 min-h-screen flex flex-col items-center p-6">
      {/* Header */}
      <header className="w-full max-w-5xl mx-auto p-6 rounded-lg shadow-lg bg-yellow-400 flex flex-col sm:flex-row justify-between items-center mb-12">
        <h1 className="text-3xl sm:text-4xl font-semibold text-gray-800 text-center sm:text-left">
          Welcome, {user?.Name || user?.Email || "Student"}!
        </h1>
        <button
          onClick={handleLogout}
          className="bg-yellow-600 text-white px-6 py-2 rounded-lg font-semibold mt-4 sm:mt-0 transition-all duration-300 ease-in-out hover:bg-yellow-700 focus:outline-none"
        >
          Logout
        </button>
      </header>

      {/* Main Content */}
      <main className="w-full max-w-5xl mx-auto p-6 bg-white rounded-lg shadow-xl">
      <div className="border border-yellow-500 p-4 mb-4">
        {/* Header with Logo and School Details */}
        <div className="md:flex justify-between items-center gap-4">
          {/* Left Section: Logo and School Details */}
          <div className="md:flex items-center gap-4 w-full md:w-auto">
            <img
              src={HOME_PIC1} // Replace with the actual path to your logo
              alt="School Logo"
              className="w-20 h-20 object-contain mx-auto md:mx-0"
            />
            <div className="text-center md:text-left">
              <h1 className="font-bold uppercase text-lg md:text-xl">Glory & Praise Prime Academy Foundation</h1>
              <p className="text-sm md:text-base">Plot 284, Glory & Praise Avenue, Kuje Abuja.</p>
              <p className="text-sm md:text-base">
                P.O. Box: 40. <a href="https://gppaf.vercel.app/" className="text-yellow-500">gppaf.vercel.app</a>
              </p>
              <p className="text-xs md:text-sm font-bold uppercase">
                Motto: Progress & Success Through Discipline & Determination
              </p>
            </div>
          </div>

          {/* Right Section: Student Info */}
          <div className="border border-yellow-500 p-3 text-xs md:text-sm w-full md:w-2/6">
            <p className="font-semibold"><strong>Name:</strong> {user?.Name || "Not available"}</p>
            <p className="font-semibold"><strong>Serial No:</strong> GPPAF/{user?.Class || "Not available"}/{user?.id || "Not available"}</p>
            <p className="font-semibold"><strong>Class:</strong> {user?.Class || "Not available"}</p>
            <p className="font-semibold"><strong>Section:</strong> {user?.Section || "Not available"}</p>
            <p className="font-semibold"><strong>Term:</strong> 1st Term</p>
            <p className="font-semibold"><strong>Session:</strong> 2024/2025</p>
            <p className="font-semibold"><strong>Resumption Date:</strong> 6th January, 2025.</p>
          </div>
        </div>

        {/* Mark Overview Section */}
        <div className="border-t border-yellow-500 mt-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs md:text-sm">
            <div>
              <p><strong>Mark Obtainable:</strong> 1600</p>
              <p><strong>Mark Obtained:</strong> {markObtained || "Not available"}</p>
            </div>
            <div>
              <p><strong>Average Score:</strong> {averageScore || "Not available"}</p>
              <p><strong>Grade:</strong> B2</p>
            </div>
            <div className="text-right md:text-left">
              {/* <p><strong>Grade:</strong> B2</p> */}
            </div>
          </div>
        </div>
      </div>

        {/* Results Section */}
        <section>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-6">
            Your Results
          </h2>

          {loading ? (
            <p className="text-lg animate-pulse">Loading data...</p>
          ) : error ? (
            <div className="bg-yellow-300 text-yellow-800 p-4 rounded-lg mb-6">
              <p className="text-lg font-bold">{error}</p>
            </div>
          ) : results.length > 0 ? (
            <div>
              <div className="overflow-x-auto">
                <table className="w-full text-left table-auto border-collapse">
                  <thead>
                    <tr className="bg-yellow-200 text-center">
                      <th className="border px-4 py-2 font-bold">Subject</th>
                      <th className="border px-4 py-2 font-bold">CA1<br />10%</th>
                      <th className="border px-4 py-2 font-bold">CA2<br />10%</th>
                      <th className="border px-4 py-2 font-bold">Classwork<br />10%</th>
                      <th className="border px-4 py-2 font-bold">Project<br />10%</th>
                      <th className="border px-4 py-2 font-bold">Exam<br />60%</th>
                      <th className="border px-4 py-2 font-bold">Score<br />100%</th>
                      <th className="border px-4 py-2 font-bold">Grade</th>
                      <th className="border px-4 py-2 font-bold">Remark</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((result, index) => (
                      <tr key={index} className="bg-white">
                        <td className="border px-4 py-2 font-medium">{result.Subject}</td>
                        <td className="border px-4 py-2 font-medium">{result.CA1}</td>
                        <td className="border px-4 py-2 font-medium">{result.CA2}</td>
                        <td className="border px-4 py-2 font-medium">{result.ClassWork}</td>
                        <td className="border px-4 py-2 font-medium">{result.Project}</td>
                        <td className="border px-4 py-2 font-medium">{result.Exam}</td>
                        <td className="border px-4 py-2 font-medium">{result.Score}</td>
                        <td className="border px-4 py-2 text-center font-medium">
                        {user.Section === "Senior-Secondary" || user.Section === "Junior-Secondary" ?
                            // Grading scheme for Senior Secondary section
                            result.Score >= 90
                            ? "A1"
                            : result.Score >= 80
                            ? "B2"
                            : result.Score >= 75
                            ? "B3"
                            : result.Score >= 70
                            ? "C4"
                            : result.Score >= 65
                            ? "C5"
                            : result.Score >= 60
                            ? "C6"
                            : result.Score >= 55
                            ? "D7"
                            : result.Score >= 50
                            ? "E8"
                            : "F9"
                          : 
                            // Default grading scheme if Section is unknown
                            result.Score >= 80
                            ? "A1"
                            : result.Score >= 70
                            ? "B2"
                            : result.Score >= 65
                            ? "B3"
                            : result.Score >= 60
                            ? "C4"
                            : result.Score >= 55
                            ? "C5"
                            : result.Score >= 50
                            ? "C6"
                            : result.Score >= 45
                            ? "D7"
                            : result.Score >= 40
                            ? "E8"
                            : "F9"
                          }
                        </td>
                        <td className="border px-4 py-2 font-medium">{result.Comment}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
                          {/* Summary Section */}
              <div className="md:flex md:space-x-20 border-t mt-2">
                <p className="py-2 text-sm sm:text-base">
                  <strong>Class Manager's Remark:</strong> Hardworking and Dedicated
                </p>
                <p className="py-2 text-sm sm:text-base">
                  <strong>Head of School's Remark:</strong> {user?.Remark}
                </p>
              </div>
              <div className="md:flex  justify-between space-y-2 md:space-y-0 md:space-x-2 items-center mt-4">
                {/* Principal Stamp */}
                <div className="space-y-2 md:w-1/3 h-15/16">
                  <p className="font-semibold pb-2">Head of School's Stamp:</p>
                  <img
                    src={HOS_STAMP} // Replace with the actual path to your logo
                    alt="School Logo"
                    className="w-full object-contain mx-auto md:mx-0"
                  />
                </div>

                {/* Grading System */}
                { user.Section === "Senior-Secondary" || user.Section === "Junior-Secondary" ?
                <table className="w-full md:w-1/3 text-left table-auto border-collapse mt-4">
                  <thead>
                    <tr className="bg-yellow-200">
                      <th className="border px-4 py-2 text-sm sm:text-base">Grade</th>
                      <th className="border px-4 py-2 text-sm sm:text-base">Score Range</th>
                    </tr>
                  </thead>
                  <tbody>                    
                    <tr className="bg-white">
                      <td className="border px-4 py-2 text-sm sm:text-base">A1</td>
                      <td className="border px-4 py-2 text-sm sm:text-base">90 - 100%</td>
                    </tr>
                    <tr className="bg-white">
                      <td className="border px-4 py-2 text-sm sm:text-base">B2</td>
                      <td className="border px-4 py-2 text-sm sm:text-base">80 - 89%</td>
                    </tr>
                    <tr className="bg-white">
                      <td className="border px-4 py-2 text-sm sm:text-base">B3</td>
                      <td className="border px-4 py-2 text-sm sm:text-base">75 - 79%</td>
                    </tr>
                    <tr className="bg-white">
                      <td className="border px-4 py-2 text-sm sm:text-base">C4</td>
                      <td className="border px-4 py-2 text-sm sm:text-base">70 - 74%</td>
                    </tr>
                    <tr className="bg-white">
                      <td className="border px-4 py-2 text-sm sm:text-base">C5</td>
                      <td className="border px-4 py-2 text-sm sm:text-base">65 - 69%</td>
                    </tr>
                    <tr className="bg-white">
                      <td className="border px-4 py-2 text-sm sm:text-base">C6</td>
                      <td className="border px-4 py-2 text-sm sm:text-base">60 - 64%</td>
                    </tr>
                    <tr className="bg-white">
                      <td className="border px-4 py-2 text-sm sm:text-base">D7</td>
                      <td className="border px-4 py-2 text-sm sm:text-base">55 - 59%</td>
                    </tr>
                    <tr className="bg-white">
                      <td className="border px-4 py-2 text-sm sm:text-base">E8</td>
                      <td className="border px-4 py-2 text-sm sm:text-base">50 - 54%</td>
                    </tr>
                    <tr className="bg-white">
                      <td className="border px-4 py-2 text-sm sm:text-base">F9</td>
                      <td className="border px-4 py-2 text-sm sm:text-base">0 - 49%</td>
                    </tr>
                  </tbody>
                </table>
                :
                <table className="w-full md:w-3/5 text-left table-auto border-collapse mt-4">
                  <thead>
                    <tr className="bg-yellow-200">
                      <th className="border px-4 py-2 text-sm sm:text-base">Grade</th>
                      <th className="border px-4 py-2 text-sm sm:text-base">Score Range</th>
                    </tr>
                  </thead>
                  <tbody>                    
                    <tr className="bg-white">
                      <td className="border px-4 py-2 text-sm sm:text-base">A1</td>
                      <td className="border px-4 py-2 text-sm sm:text-base">80 - 100%</td>
                    </tr>
                    <tr className="bg-white">
                      <td className="border px-4 py-2 text-sm sm:text-base">B2</td>
                      <td className="border px-4 py-2 text-sm sm:text-base">70 - 79%</td>
                    </tr>
                    <tr className="bg-white">
                      <td className="border px-4 py-2 text-sm sm:text-base">B3</td>
                      <td className="border px-4 py-2 text-sm sm:text-base">65 - 69%</td>
                    </tr>
                    <tr className="bg-white">
                      <td className="border px-4 py-2 text-sm sm:text-base">C4</td>
                      <td className="border px-4 py-2 text-sm sm:text-base">60 - 64%</td>
                    </tr>
                    <tr className="bg-white">
                      <td className="border px-4 py-2 text-sm sm:text-base">C5</td>
                      <td className="border px-4 py-2 text-sm sm:text-base">55 - 59%</td>
                    </tr>
                    <tr className="bg-white">
                      <td className="border px-4 py-2 text-sm sm:text-base">C6</td>
                      <td className="border px-4 py-2 text-sm sm:text-base">50 - 54%</td>
                    </tr>
                    <tr className="bg-white">
                      <td className="border px-4 py-2 text-sm sm:text-base">D7</td>
                      <td className="border px-4 py-2 text-sm sm:text-base">45 - 49%</td>
                    </tr>
                    <tr className="bg-white">
                      <td className="border px-4 py-2 text-sm sm:text-base">E8</td>
                      <td className="border px-4 py-2 text-sm sm:text-base">40 - 44%</td>
                    </tr>
                    <tr className="bg-white">
                      <td className="border px-4 py-2 text-sm sm:text-base">F9</td>
                      <td className="border px-4 py-2 text-sm sm:text-base">0 - 39%</td>
                    </tr>
                  </tbody>
                </table>
                }
                {/* Counselor Stamp */}
                <div className="space-y-2 h-15/16 md:w-1/3 ">
                  <p className="font-semibold pb-2">Counselor's Stamp:</p>
                  <div className="relative h-max p-2 space-y-2 border border-dashed flex flex-col items-center justify-center mt-2">
                    <div className="top-2 text-center text-xs uppercase font-bold">
                      Glory & Praise Prime Academy Foundation
                    </div>
                    <div className="bottom-2 text-center text-xs uppercase font-bold pb-4">
                      Counselor
                    </div>
                    <img
                      src={Counsellor} // Replace with the actual path to your logo
                      alt="School Logo"
                      className="w-full h-20 object-contain mx-auto md:mx-0"
                    />
                    {/* <div className="w-20 h-8 border-b border-black mt-4"></div> */}
                    <p className="font-semibold border-t-2 mt-2">Signature</p>
                  </div>
                </div>
              </div>
            </div>

          ) : (
            <button
              onClick={generateResults}
              className="bg-yellow-600 text-white px-6 py-2 rounded-lg font-semibold transition-all duration-300 ease-in-out hover:bg-yellow-700 focus:outline-none"
            >
              Generate Results
            </button>
          )}
        </section>
      </main>
    </div>
  );
};

export default Dashboard;
